import { useEffect, useMemo } from "react"
import { H3HexagonLayer } from "@deck.gl/geo-layers"
import { latLngToCell } from "h3-js"
import useSWR from "swr"
import { config } from "@nova/configuration/browser"
import { decode } from "@nova/geo-trie"
import { useViewport } from "features/map/GlobalViewState"
import { useReportOverlayStatus } from "features/map/layers/overlays/useReportOverlayStatus"
import { LayerStatus } from "features/map/layers/overlays/layerStatus"

const REFRESH_INTERVAL_MS = 15 * 60 * 1000

function firmsUrl(): string {
    const environmentName = config.get("environmentName")
    if (environmentName === "production") {
        return `https://cdn.mapnova.com/firms/active-fires/latest.gtt`
    } else {
        return `https://cdn.fireai.io/firms/active-fires/latest-${environmentName}.gtt`
    }
}

async function decompressIfNeeded(buf: ArrayBuffer): Promise<Uint8Array> {
    const bytes = new Uint8Array(buf)
    if (bytes[0] !== 0x1f || bytes[1] !== 0x8b) return bytes
    const ds = new DecompressionStream("gzip")
    const writer = ds.writable.getWriter()
    writer.write(bytes)
    writer.close()
    return new Uint8Array(await new Response(ds.readable).arrayBuffer())
}

export async function fetchActiveFirePoints() {
    const res = await fetch(firmsUrl())
    if (!res.ok) throw new Error(`FIRMS fetch failed: ${res.status}`)
    const bytes = await decompressIfNeeded(await res.arrayBuffer())
    return decode(bytes)
}

const H3_RESOLUTIONS = [4, 5, 6, 7, 8] as const
export type H3Resolution = (typeof H3_RESOLUTIONS)[number]

export function h3ResForZoom(zoom: number): H3Resolution {
    if (zoom < 6) return 4
    if (zoom < 7) return 5
    if (zoom < 8) return 6
    if (zoom < 9) return 7
    return 8
}

export type H3Cell = { hex: string; count: number }

export function heatOpacityForResolution(resolution: H3Resolution): number {
    return Math.min(0.5 + (8 - resolution) * 0.4, 1)
}

export const HEAT_HEX_COVERAGE = 0.85

export function heatFillColorForCount(
    count: number,
    maxCount: number
): [number, number, number, number] {
    const t = Math.min(Math.log2(count + 1) / Math.log2(maxCount / 2 + 1), 1)
    return [255, Math.round(130 - 100 * t), 0, Math.round(155 + 100 * t)]
}

export function aggregateToH3(points: Array<[number, number]>, resolution: number): H3Cell[] {
    const map = new Map<string, number>()
    for (const [lat, lon] of points) {
        const hex = latLngToCell(lat, lon, resolution)
        map.set(hex, (map.get(hex) ?? 0) + 1)
    }
    const cells: H3Cell[] = []
    map.forEach((count, hex) => cells.push({ hex, count }))
    return cells
}

export function useHeatLayer(enabled: boolean): {
    layer: H3HexagonLayer | null
    status: LayerStatus
} {
    const viewport = useViewport()
    const zoom = viewport?.zoom ?? 4

    const {
        data: points,
        error,
        isLoading,
    } = useSWR(enabled ? "heat-active-fires" : null, fetchActiveFirePoints, {
        refreshInterval: REFRESH_INTERVAL_MS,
        revalidateOnFocus: false,
    })

    useEffect(() => {
        if (error) console.error("[heat] failed to load active fires:", error)
    }, [error])

    const status: LayerStatus = !enabled
        ? "idle"
        : error
          ? "error"
          : isLoading
            ? "loading"
            : "ready"

    const hexCache = useMemo(() => {
        if (!points) return null
        const cache = new Map<H3Resolution, H3Cell[]>()
        for (const res of H3_RESOLUTIONS) {
            cache.set(res, aggregateToH3(points, res))
        }
        return cache
    }, [points])

    const h3Res = h3ResForZoom(zoom)

    const layer = useMemo(() => {
        if (!enabled || !hexCache) return null

        const hexData = hexCache.get(h3Res) ?? []
        const maxCount = hexData.reduce((m, d) => Math.max(m, d.count), 1)

        return new H3HexagonLayer<H3Cell>({
            id: "heat-active-fires",
            data: hexData,
            getHexagon: (d) => d.hex,
            extruded: false,
            stroked: false,
            coverage: HEAT_HEX_COVERAGE,
            opacity: heatOpacityForResolution(h3Res),
            getFillColor: (d) => heatFillColorForCount(d.count, maxCount),
            pickable: false,
            updateTriggers: {
                getFillColor: [maxCount],
            },
        })
    }, [enabled, hexCache, h3Res])

    useReportOverlayStatus("heat", status)

    return { layer, status }
}
